import { useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

type EventType = 'search' | 'ai_consultant' | 'blog_view' | 'cart_add' | 'page_view';

interface DeviceInfo {
  device_type: string;
  browser: string;
  os: string;
  screen_resolution: string;
  referrer: string;
}

const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

const getDeviceInfo = (): DeviceInfo => {
  const ua = navigator.userAgent;
  
  let device_type = 'desktop';
  if (/Mobi|Android/i.test(ua)) device_type = 'mobile';
  else if (/Tablet|iPad/i.test(ua)) device_type = 'tablet';
  
  let browser = 'unknown';
  if (ua.includes('Firefox/')) browser = 'Firefox';
  else if (ua.includes('Edg/')) browser = 'Edge';
  else if (ua.includes('Chrome/')) browser = 'Chrome';
  else if (ua.includes('Safari/')) browser = 'Safari';
  else if (ua.includes('Opera') || ua.includes('OPR/')) browser = 'Opera';
  
  let os = 'unknown';
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  
  return {
    device_type,
    browser,
    os,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    referrer: document.referrer || '',
  };
};

// Cache IP address per session to avoid repeated API calls
let cachedIp: string | null = null;
const getIpAddress = async (): Promise<string | null> => {
  if (cachedIp) return cachedIp;
  try {
    const res = await fetch('https://api.ipify.org?format=json');
    const data = await res.json();
    cachedIp = data.ip || null;
    return cachedIp;
  } catch {
    return null;
  }
};

export const useAnalytics = () => {
  const pendingRef = useRef<Set<string>>(new Set());

  const trackEvent = useCallback(async (
    eventType: EventType,
    metadata?: Record<string, any>
  ) => {
    try {
      // Deduplicate rapid-fire same events
      const dedupeKey = `${eventType}_${JSON.stringify(metadata || {})}`;
      if (pendingRef.current.has(dedupeKey)) return;
      pendingRef.current.add(dedupeKey);
      setTimeout(() => pendingRef.current.delete(dedupeKey), 2000);

      const sessionId = getSessionId();
      const deviceInfo = getDeviceInfo();
      
      const [{ data: { user } }, ipAddress] = await Promise.all([
        supabase.auth.getUser(),
        getIpAddress(),
      ]);

      await supabase.from('analytics_events').insert({
        event_type: eventType,
        session_id: sessionId,
        user_id: user?.id || null,
        ip_address: ipAddress,
        device_type: deviceInfo.device_type,
        browser: deviceInfo.browser,
        os: deviceInfo.os,
        screen_resolution: deviceInfo.screen_resolution,
        referrer: deviceInfo.referrer,
        page_url: window.location.pathname,
        metadata: metadata || {},
      });
    } catch (error) {
      // Silently fail - analytics should never break the app
      console.debug('Analytics tracking error:', error);
    }
  }, []);

  return { trackEvent };
};

// Standalone function for use outside React components
export const trackAnalyticsEvent = async (
  eventType: EventType,
  metadata?: Record<string, any>
) => {
  try {
    const sessionId = getSessionId();
    const deviceInfo = getDeviceInfo();
    const [{ data: { user } }, ipAddress] = await Promise.all([
      supabase.auth.getUser(),
      getIpAddress(),
    ]);

    await supabase.from('analytics_events').insert({
      event_type: eventType,
      session_id: sessionId,
      user_id: user?.id || null,
      ip_address: ipAddress,
      device_type: deviceInfo.device_type,
      browser: deviceInfo.browser,
      os: deviceInfo.os,
      screen_resolution: deviceInfo.screen_resolution,
      referrer: deviceInfo.referrer,
      page_url: window.location.pathname,
      metadata: metadata || {},
    });
  } catch (error) {
    console.debug('Analytics tracking error:', error);
  }
};
